import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./AgentSchedule.module.css";
import HeaderBar from "../HeaderBar/HeaderBar";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import {
  getBusinessHoursByBusinessId,
  updateBusinessHoursByBusinessId,
  getAgentSettingsSection,
  updateAgentSettingsSection,
} from "../../Store/apiStore";
import PopUp from "../Popup/Popup";
import Loader2 from "../Loader2/Loader2";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DEFAULT_TIME = { open: "00:00", close: "23:59", closed: false };

// ✅ Slot config
const SLOT_STEP_MIN = 15;

function pad2(n) {
  return String(n).padStart(2, "0");
}

function timeToMinutes(hhmm) {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(mins) {
  const safe = ((mins % (24 * 60)) + (24 * 60)) % (24 * 60);
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

function generateSlots(stepMin = 15) {
  const slots = [];
  for (let mins = 0; mins < 24 * 60; mins += stepMin) {
    slots.push(minutesToTime(mins));
  }
  return slots;
}

// ---------- existing helpers ----------
function to24h(str) {
  const s = String(str).replace(/\u202f/g, " ").replace(/\s+/g, " ").trim();

  // already HH:mm
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(":").map(Number);
    return `${String(Math.min(Math.max(h, 0), 23)).padStart(2, "0")}:${String(
      Math.min(Math.max(m, 0), 59)
    ).padStart(2, "0")}`;
  }

  // h:mm AM/PM
  const match = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return "09:00";

  let h = parseInt(match[1], 10);
  const m = parseInt(match[2] || "00", 10);
  const ap = match[3].toUpperCase();

  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function to12h(hhmm) {
  const [H, M] = String(hhmm || "09:00").split(":");
  let h = parseInt(H || "9", 10);
  const m = parseInt(M || "0", 10);

  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;

  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
}

function parseHoursString(hoursRaw = "") {
  const map = {};
  const noHours =
    !hoursRaw || typeof hoursRaw !== "string" || hoursRaw.trim().length === 0;

  DAYS.forEach((d) => {
    map[d] = noHours ? { open: "00:00", close: "23:59", closed: false } : { ...DEFAULT_TIME };
  });
  if (noHours) return map;

  const blocks = hoursRaw
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);

  for (const block of blocks) {
    const idx = block.indexOf(":");
    if (idx < 0) continue;

    const day = block.slice(0, idx).trim();
    const timePart = block.slice(idx + 1).trim();
    if (!map[day]) continue;

    if (/closed/i.test(timePart)) {
      map[day] = { open: "", close: "", closed: true };
      continue;
    }

    const [openStr, closeStr] = timePart.split(/–|-/).map((s) => s.trim());
    map[day] = {
      open: to24h(openStr || "09:00 AM"),
      close: to24h(closeStr || "06:00 PM"),
      closed: false,
    };
  }

  return map;
}

function buildHoursString(map) {
  return DAYS.map((day) => {
    const v = map[day] || DEFAULT_TIME;
    if (v.closed) return `${day}: Closed`;
    return `${day}: ${to12h(v.open)} – ${to12h(v.close)}`;
  }).join(" | ");
}

/** ✅ Inline Custom Time Picker (same page, no extra file)
 * - white dropdown
 * - scrollbar hidden
 * - fixed max height
 */
function TimeDropdown({
  value,
  options,
  onSelect,
  disabled,
  maxHeight = 260,
  chipClassName,
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    function closeOnOutside(e) {
      if (!open) return;
      const t = e.target;
      if (popRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("touchstart", closeOnOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("touchstart", closeOnOutside);
    };
  }, [open]);

  // scroll selected into view when open
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      const el = popRef.current?.querySelector(`[data-val="${value}"]`);
      el?.scrollIntoView({ block: "center" });
    });
  }, [open, value]);

  return (
    <span className={styles.timeWrap}>
      <button
        ref={btnRef}
        type="button"
        className={`${styles.timeChip} ${chipClassName || ""} ${disabled ? styles.timeChipDisabled : ""}`}
        onClick={() => !disabled && setOpen((p) => !p)}
        disabled={disabled}
      >
        {value ? to12h(value) : "--"}
        {!disabled && <span className={styles.timeChevron}>▾</span>}
      </button>

      {open && !disabled && (
        <div
          ref={popRef}
          className={styles.timePop}
          style={{ maxHeight }}
          role="listbox"
        >
          {options.map((t) => (
            <button
              key={t}
              type="button"
              className={`${styles.timeItem} ${t === value ? styles.timeItemActive : ""}`}
              data-val={t}
              onClick={() => {
                onSelect(t);
                setOpen(false);
              }}
            >
              {to12h(t)}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

const AgentSchedule = () => {
  const businessId = sessionStorage.getItem("SelectAgentBusinessId");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ✅ PopUp states
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");

  const [sameEveryday, setSameEveryday] = useState(false);
  const [is24x7, setIs24x7] = useState(true);

  // Load is24x7 from AgentSettings
  useEffect(() => {
    const agentId = sessionStorage.getItem("SelectAgentId") || "";
    if (!agentId) return;
    (async () => {
      try {
        const data = await getAgentSettingsSection(agentId, "availability");
        setIs24x7(data?.is24x7 !== false);
      } catch {}
    })();
  }, []);

  const [hoursMap, setHoursMap] = useState(() => parseHoursString(""));

  const hoursPreview = useMemo(() => buildHoursString(hoursMap), [hoursMap]);

  const prevMapRef = useRef(null);
  const prevSameRef = useRef(false);

  const initialHoursRef = useRef("");
  const [confirmSameEveryday, setConfirmSameEveryday] = useState(false);


  const isDirty = useMemo(() => {
    const initial = (initialHoursRef.current || "").trim();
    const current = (hoursPreview || "").trim();
    return initial !== current;
  }, [hoursPreview]);

  // ✅ time slots once (15 min)
  const TIME_SLOTS = useMemo(() => generateSlots(SLOT_STEP_MIN), []);

  const handleTimeChange = (day, field, value) => {
    // hours always editable — is24x7 only controls agent availability, not hours UI

    setHoursMap((prev) => {
      const curr = prev[day] || DEFAULT_TIME;

      let updated = { ...curr, [field]: value, closed: false };

      // ✅ ensure close > open (auto-fix)
      if (field === "open") {
        const openM = timeToMinutes(value || "09:00");
        const closeM = timeToMinutes(curr.close || "18:00");
        if (closeM <= openM) {
          updated.close = minutesToTime(openM + SLOT_STEP_MIN);
        }
      }

      // ✅ if close <= open, force close to next slot
      if (field === "close") {
        const openM = timeToMinutes(curr.open || "09:00");
        const closeM = timeToMinutes(value || "18:00");
        if (closeM <= openM) {
          updated.close = minutesToTime(openM + SLOT_STEP_MIN);
        }
      }

      const next = { ...prev, [day]: updated };

      if (sameEveryday) {
        const mo = updated.open || "09:00";
        const mc = updated.close || "18:00";
        DAYS.forEach((d) => {
          if (!next[d]?.closed) {
            next[d] = { ...next[d], open: mo, close: mc, closed: false };
          }
        });
      }

      return next;
    });
  };

  // ✅ GET business hours
  useEffect(() => {
    if (!businessId) {
      setLoading(false);
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage("BusinessId missing (session SelectAgentBusinessId)");
      return;
    }

    (async () => {
      try {
        setLoading(true);

        const data = await getBusinessHoursByBusinessId(businessId);
        const parsed = parseHoursString(data?.hoursRaw || "");

        setHoursMap(parsed);
        setTimeout(() => {
          initialHoursRef.current = buildHoursString(parsed).trim();
        }, 0);

        // is24x7 loaded from AgentSettings useEffect — not derived from hours
        setSameEveryday(false);
      } catch (e) {
        setShowPopup(true);
        setPopupType("failed");
        setPopupMessage(e?.response?.data?.error || e?.message || "Failed to fetch hours");
      } finally {
        setLoading(false);
      }
    })();
  }, [businessId]);

  const toggleDay = (day) => {
    // hours always editable — is24x7 only controls agent availability, not hours UI

    setHoursMap((prev) => {
      const curr = prev[day] || DEFAULT_TIME;
      const nextClosed = !curr.closed;

      const next = {
        ...prev,
        [day]: nextClosed
          ? { open: "", close: "", closed: true }
          : {
            open: curr.open || "09:00",
            close: curr.close || "18:00",
            closed: false,
          },
      };

      if (sameEveryday) {
        const master = DAYS.find((d) => !next[d]?.closed);
        const mo = master ? next[master].open : "09:00";
        const mc = master ? next[master].close : "18:00";
        DAYS.forEach((d) => {
          if (!next[d]?.closed) {
            next[d] = { ...next[d], open: mo, close: mc, closed: false };
          }
        });
      }

      return next;
    });
  };

  const handleSameEveryday = () => {
    // hours always editable — is24x7 only controls agent availability, not hours UI

    setSameEveryday((prev) => {
      const nextVal = !prev;

      if (nextVal) {
        setHoursMap((old) => {
          const next = { ...old };
          const master = DAYS.find((d) => !next[d]?.closed);
          const mo = master ? next[master].open : "09:00";
          const mc = master ? next[master].close : "18:00";
          DAYS.forEach((d) => {
            if (!next[d]?.closed) {
              next[d] = { ...next[d], open: mo, close: mc, closed: false };
            }
          });
          return next;
        });
      }

      return nextVal;
    });
  };

  // ✅ 24x7 — only toggles availability, does NOT change business hours
  const handle24x7 = async () => {
    const nextVal = !is24x7;
    setIs24x7(nextVal);
    // Save to AgentSettings
    const agentId = sessionStorage.getItem("SelectAgentId") || "";
    if (agentId) {
      try {
        await updateAgentSettingsSection(agentId, "availability", { is24x7: nextVal });
      } catch (e) {
        console.error("[AgentSchedule] save is24x7 failed:", e);
      }
    }
  };

  // ✅ SAVE business hours
  const handleSave = async () => {
    if (!isDirty || saving || loading) return;
    try {
      if (!businessId) {
        setShowPopup(true);
        setPopupType("failed");
        setPopupMessage("BusinessId missing");
        return;
      }

      setSaving(true);

      await updateBusinessHoursByBusinessId(businessId, hoursPreview);

      setShowPopup(true);
      setPopupType("success");
      setPopupMessage("Business hours saved");
      initialHoursRef.current = hoursPreview.trim();
    } catch (e) {
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage(e?.response?.data?.error || e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.ConatinerBox}>
      <div className={styles.Forsticky}>

        <HeaderBar
          title="Agent Schedule"
          backgroundColor="#6524EB"
          color="#ffffff"
        />

      </div>
      {/* 🔥 PAGE LOADER OVERLAY */}
      {loading && (
        <div className={styles.pageLoader}>
          <Loader2 />
        </div>
      )}

      <div className={styles.container}>
        {/* 24x7 Agent Active */}
        <div className={styles.scheduleModalHeader2}>
          <h4>24x7 Agent Active</h4>
          <label className={styles.scheduleModalDay}>
            <input
              type="checkbox"
              checked={is24x7}
              onChange={handle24x7}
            />
          </label>
        </div>

        <p className={styles.scheduleModalSubText}>
          {is24x7
            ? "Your agent will answer all calls 24/7. Outside business hours, the agent will take messages and book appointments on your behalf."
            : "Your agent will only answer calls during business hours. Outside these hours, unknown callers will be rejected. Enable 24/7 to let your agent handle calls anytime — even after hours."}
        </p>

        <div className={styles.divider2} />

        {/* Business Hours Section */}
        <h4 style={{ fontSize: 15, fontWeight: 600, color: "#333", margin: "12px 0 8px" }}>Business Hours</h4>

        <div className={styles.scheduleModalHeader}>
          <h4>Same Schedule Everyday</h4>
          <label className={styles.scheduleModalToggle}>
            <input
              type="checkbox"
              checked={sameEveryday}
              disabled={false}
              onChange={() => {
                setConfirmSameEveryday(true);
                setShowPopup(true);
                setPopupType("confirm");
                setPopupMessage(
                  "Are you sure you want to apply the same schedule to all days?"
                );
              }}
            />
            <span />
          </label>
        </div>

        <p className={styles.scheduleModalSubText}>
          Apply same daily schedule to all the days selected
        </p>

        {/* Schedule Rows */}
        <div className={styles.dayMainDiv}>
          {DAYS.map((day) => {
            const v = hoursMap[day] || DEFAULT_TIME;

            const openMins = timeToMinutes(v.open || "09:00");
            const closeOptions = TIME_SLOTS.filter(
              (t) => timeToMinutes(t) > openMins
            );

            const safeClose = v.closed
              ? ""
              : timeToMinutes(v.close || "18:00") > openMins
                ? v.close
                : minutesToTime(openMins + SLOT_STEP_MIN);

            return (
              <div key={day} className={styles.scheduleModalRow}>
                <label className={styles.scheduleModalDay}>
                  <input
                    type="checkbox"
                    checked={!v.closed}
                    disabled={false}
                    onChange={() => toggleDay(day)}
                  />
                  <span>{day}</span>
                </label>

                <div className={styles.scheduleModalTime}>
                    <>
                      {/* OPEN */}
                      {v.closed ? (
                        <span
                          className={styles.dayfrom}
                          style={{ opacity: 0.6 }}
                        >
                          --
                        </span>
                      ) : (
                        <TimeDropdown
                          value={v.open}
                          options={TIME_SLOTS}
                          onSelect={(t) =>
                            handleTimeChange(day, "open", t)
                          }
                          disabled={v.closed}
                          maxHeight={240}
                          chipClassName={styles.dayfrom}
                        />
                      )}

                      <span>-</span>

                      {/* CLOSE */}
                      {v.closed ? (
                        <span
                          className={styles.dayto}
                          style={{ opacity: 0.6 }}
                        >
                          --
                        </span>
                      ) : (
                        <TimeDropdown
                          value={safeClose}
                          options={closeOptions}
                          onSelect={(t) =>
                            handleTimeChange(day, "close", t)
                          }
                          disabled={v.closed}
                          maxHeight={240}
                          chipClassName={styles.dayto}
                        />
                      )}
                    </>
                </div>
              </div>
            );
          })}
        </div>

        {/* Save button ke peeche white bar — neeche ki list button ke through
            na dikhe. Button wrapper se alag rakha hai kyunki disabled state me
            wrapper opacity 0.5 leta hai, bar bhi transparent ho jaati. */}
        <div className={styles.saveBar} />

        {/* SAVE BUTTON */}
        <div
          className={`${styles.scheduleButton} ${!isDirty || saving || loading ? styles.disabledBtn : ""
            }`}
          style={{
            pointerEvents:
              !isDirty || saving || loading ? "none" : "auto",
            opacity:
              !isDirty || saving || loading ? 0.5 : 1,
            cursor:
              !isDirty || saving || loading
                ? "not-allowed"
                : "pointer",
          }}
          onClick={handleSave}
        >
          <AnimatedButton
            isLoading={saving}
            label={saving ? "Saving..." : "Save"}
          />
        </div>
      </div>

      {/* ✅ POPUP */}
      {showPopup && (
        <PopUp
          type={popupType}
          message={popupMessage}
          onConfirm={() => {
            if (confirmSameEveryday) {
              handleSameEveryday();
              setConfirmSameEveryday(false);
            }
            setShowPopup(false);
          }}
          onClose={() => {
            setConfirmSameEveryday(false);
            setShowPopup(false);
            setPopupMessage("");
            setPopupType(null);
          }}
        />
      )}
    </div>
  );

};

export default AgentSchedule;
